import { useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import CRTargetingPanel from "./CRTargetingPanel";

/**
 * AdvancedTargetingDrawer — right-side slide-in drawer that hosts the full
 * PureSpectrum attribute browser. Progressive disclosure per product spec:
 * the main wizard shows only clean "+ Add Definition" tiles; the overwhelming
 * 1,000+ filter catalog stays hidden until the user asks for it.
 *
 * Behaviour:
 *   - Slides in from the right on `open=true`.
 *   - Dismissible via close button, Esc key, or backdrop click.
 *   - Locks the body scroll while open.
 *   - The Feasibility Ring remains visible to the LEFT so users see live
 *     feasibility shrinkage as they toggle filters — a premium experience
 *     modelled on Notion / Linear filter drawers.
 */
export default function AdvancedTargetingDrawer({
  open,
  onClose,
  value,
  onChange,
  prefetchedData,
  testid = "advanced-targeting-drawer",
}) {
  // Esc-to-close.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Lock body scroll while the drawer is open so the page beneath stays put.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  return createPortal(
    <>
      {/* Backdrop — z-[9998], rendered via portal to document.body so it
          bypasses any parent stacking context (the sticky nav's
          backdrop-filter would otherwise render above numeric z-indexes). */}
      <div
        className={
          "fixed inset-0 bg-black/50 backdrop-blur-[2px] z-[9998] transition-opacity duration-200 " +
          (open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none")
        }
        onClick={onClose}
        aria-hidden="true"
        data-testid={`${testid}-backdrop`}
      />
      {/* Panel — z-[9999] to sit on top of EVERYTHING including the sticky nav. */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Advanced targeting definitions"
        className={
          "fixed top-0 right-0 h-full w-full sm:w-[520px] lg:w-[600px] bg-white " +
          "border-l-4 border-[var(--rl-text)] shadow-[-8px_0_0_var(--rl-brand)] z-[9999] " +
          "flex flex-col transition-transform duration-300 ease-out " +
          (open ? "translate-x-0" : "translate-x-full")
        }
        data-testid={testid}
        data-open={open ? "true" : "false"}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-[var(--rl-border)] bg-[var(--rl-bg-subtle)]">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] font-bold text-[var(--rl-text)]">Advanced targeting</div>
            <div className="text-[10px] font-mono text-[var(--rl-text-secondary)] mt-0.5">
              Pick a topic · pick who to include · apply
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close drawer"
            className="ml-auto inline-flex items-center justify-center w-9 h-9 border border-[var(--rl-border)] bg-white hover:border-[var(--rl-text)] hover:-translate-y-px transition-all"
            data-testid={`${testid}-close`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        {/* Body — CRTargetingPanel handles catalog rendering + selection. */}
        <div className="flex-1 overflow-y-auto min-h-0" data-testid={`${testid}-body`}>
          {open && (
            <CRTargetingPanel
              value={value}
              onChange={onChange}
              prefetchedData={prefetchedData}
              testid="cr-targeting-panel"
            />
          )}
        </div>
        {/* Footer — prominent "Done" primary action. Uses the brand accent
            so the CTA reads clearly against the white drawer chrome, with
            a chunky shadow that mirrors the wizard's other primary buttons. */}
        <div className="flex items-center gap-4 px-5 py-4 border-t-2 border-[var(--rl-text)] bg-[var(--rl-bg-subtle)]">
          <div className="text-[11px] font-mono uppercase tracking-[0.15em] text-[var(--rl-text-secondary)]">
            {value?.length ? `${value.length} definition${value.length === 1 ? "" : "s"} applied` : "No definitions applied yet"}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="ml-auto px-6 py-3 bg-[var(--rl-brand)] text-white text-sm uppercase tracking-[0.25em] font-bold shadow-[4px_4px_0_var(--rl-text)] hover:shadow-[6px_6px_0_var(--rl-text)] hover:-translate-y-0.5 transition-all"
            data-testid={`${testid}-done`}
          >
            Done
          </button>
        </div>
      </aside>
    </>,
    document.body,
  );
}
